import { useState, useEffect, useMemo, useCallback } from "react";
import { invoiceNavRows } from "src/helpers/invoiceNavigation";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import "./invoices.scss";
import "./invoiceFilterBar.scss";
import "../../styles/tables.scss";

import { SortIcon } from "src/components/SortIcon/SortIcon";
import InvoiceRow from "./InvoiceRow";
import PageHeader from "src/components/PageHeader/PageHeader";
import RosterSubBar from "src/components/PageHeader/RosterSubBar";
import TablePagination from "src/components/TablePagination/TablePagination";
import TableSkeleton from "src/components/TableSkeleton/TableSkeleton";
import Toast from "src/components/Toast/Toast";
import Popup from "src/components/Popup/Popup";
import EmptyState from "src/components/EmptyState/EmptyState";
import InvoiceFilterBar, { matchesOrigin, type InvoiceFilters } from "./InvoiceFilterBar";

import { FaUpload } from "react-icons/fa";
import { MdOutlineWarningAmber, MdOutlineInfo } from "react-icons/md";
import { TbHistory, TbDownload, TbTag, TbFileEuro, TbLink, TbFilePlus, TbFilePencil } from "react-icons/tb";
import { InvoiceDraftsPopup, type InvoiceDraftSummary } from "./InvoiceDraftsPopup";
import { useNavigate } from "react-router-dom";
import { SlEnvolopeLetter } from "react-icons/sl";
import { RiDeleteBin2Line } from "react-icons/ri";

import { useInvoiceList, InvoiceSortKey, getSortValue, isStatusSendable, hasEmail, isReadyForPost } from "./useInvoiceList";
import { invoiceMatchesSearch } from "src/utils/invoiceSearch";
import { InvoiceStatus } from "src/constants/invoice";
import { computeInvoiceViewCounts, matchesInvoiceView, type InvoiceViewKey } from "./invoiceListViews";
import { useClientPagination } from "src/hooks/useClientPagination";
import { apiRequest } from "src/services/apiClient";
import { useApi } from "src/hooks/useApi";
import { useAuth } from "src/context/AuthProvider";
import { isStandard, isAdmin as checkAdmin } from "src/utils/roles";
import { PadUploadPopup } from "./PadUploadPopup";
import UploadHistoryPopup from "./UploadHistoryPopup";
import { CsvExportPopup } from "./CsvExportPopup";
import { ManageTagsPopup } from "./ManageTagsPopup";
import InvoiceTagsPopup from "src/pages/InvoicePage/InvoiceTagsPopup";
import type { Invoice, Tag } from "src/types";

function applyInvoiceFilters(invoices: Invoice[], search: string, filters: InvoiceFilters, requireLegalAcceptance: boolean): Invoice[] {
    return invoices.filter((i) => {
        if (i.status === InvoiceStatus.DELETED) return false;
        if (!invoiceMatchesSearch(i, search)) return false;
        if (filters.sent !== "all") {
            const isSent = !!i.sentDate;
            if (filters.sent === "sent" && !isSent) return false;
            if (filters.sent === "unsent" && isSent) return false;
        }
        if (filters.amountMin !== "" && i.amount < parseFloat(filters.amountMin)) return false;
        if (filters.dunning !== "" && String(i.dunning) !== filters.dunning) return false;
        if (filters.status !== "" && String(i.status) !== filters.status) return false;
        if (filters.patients) {
            const q = filters.patients.toLowerCase();
            const account = `${i.recipient?.firstName ?? ""} ${i.recipient?.lastName ?? ""}`.toLowerCase();
            const billed = `${i.patient?.firstName ?? ""} ${i.patient?.lastName ?? ""}`.toLowerCase();
            if (!account.includes(q) && !billed.includes(q)) return false;
        }
        if (filters.createdFrom) {
            const from = new Date(filters.createdFrom);
            from.setHours(0, 0, 0, 0);
            if (new Date(i.creationDate) < from) return false;
        }
        if (filters.createdTo) {
            const to = new Date(filters.createdTo);
            to.setHours(23, 59, 59, 999);
            if (new Date(i.creationDate) > to) return false;
        }
        // postConsentAccepted, not questionnaireAccepted — physical/postal Einwilligung consent
        // counts as accepted too, same as the backend CSV export's resolveQuestionnaireRecipientIds
        // (invoiceController.ts).
        if (filters.questionnaireSubmitted && !i.postConsentAccepted) return false;
        if (filters.questionnaireNotSubmitted && !(i.questionnaireSent && !i.postConsentAccepted)) return false;
        if (filters.questionnaireNotSent && (i.questionnaireSent || i.postConsentAccepted)) return false;
        if (filters.vatOnly && !((i.vatRate ?? 0) > 0)) return false;
        if (filters.goaConflict && !i.goaConflict) return false;
        if (filters.amountMismatch && !i.amountMismatch) return false;
        if (filters.readyForPost && !isReadyForPost(i, requireLegalAcceptance)) return false;
        if (filters.tag && !i.tagIds?.includes(filters.tag)) return false;
        if (filters.doctor && i.ownerDoctorId !== filters.doctor) return false;
        if (!matchesOrigin(filters.origin, i.manualInvoice)) return false;
        return true;
    });
}

export default function Invoices() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPro = user?.praxis?.entitlements?.goaChecker ?? (user?.praxis?.businessPlan ?? 1) >= 3;
    const isPlus = (user?.praxis?.businessPlan ?? 1) >= 2;
    const role = user?.doctor?.userRights;
    const isAdmin = checkAdmin(role);
    const canDelete = user?.doctor?.permEdit !== undefined ? user.doctor.permEdit : !isStandard(role);
    // Matches the backend's requireNotStandard guard on POST /upload (and the other actions
    // this flag gates) exactly — MedicalAssistant and Accounting are both blocked by default,
    // not just Accounting, so the button is never shown to someone whose click would 403.
    const canWrite  = user?.doctor?.permEdit !== undefined ? user.doctor.permEdit : !isStandard(role);
    const canExportCsv = user?.doctor?.permReports !== undefined
        ? user.doctor.permReports
        : role !== 1; // MedicalAssistant blocked by default
    const isTrialing = user?.praxis?.subscriptionStatus === "trialing" &&
        !!user?.praxis?.trialEndsAt &&
        new Date(user.praxis.trialEndsAt) > new Date();

    const location = useLocation();
    const [viewKey, setViewKey] = useState<InvoiceViewKey>(() => {
        const v = location.state?.viewKey;
        return (["all","open","overdue","unsent","paid","writtenOff"] as InvoiceViewKey[]).includes(v) ? v : "all";
    });

    const {
        search, setSearch,
        toast, setToast,
        filters, setFilters,
        selectedIds, setSelectedIds,
        bulkPopup, setBulkPopup,
        loading, error,
        sortKey, sortDir, handleSort,
        postEnabled,
        emailEnabled,
        questionsEnabled,
        requireLegalAcceptance,
        tagsEnabled,
        legalMode,
        hasPaywiseKey,
        paywiseConfirmed,
        invoices,
        multiDoctor,
        doctorOptions,
        lxPopupInvoiceId, setLxPopupInvoiceId,
        lxStatusJob, setLxStatusJob, checkLetterStatus,
        lxPraxis,
        lxDefaults,
        pdfUrl, pdfLoading, pdfFrameLoading, setPdfFrameLoading, closePdf,
        sendPortalLink, openLxPopup, viewPdf, sendViaLetterXpress, deleteInvoice,
        toggleSelect,
        toggleSelectAll,
        bulkSend, bulkSendLetter, bulkDelete, bulkDeleting, deletePhase, deleteCount,
        refetch,
    } = useInvoiceList();

    const showPaywise = legalMode === "api" && hasPaywiseKey && paywiseConfirmed;

    // Tags are a Plus+ feature (or admin-granted add-on), gated per-doctor by the
    // Tags & Groups view/edit permissions (admin-only by default, see TeamSettings).
    const tagsAvailable = (user?.praxis?.entitlements?.tagging ?? isPlus) && tagsEnabled;
    const canViewTags = user?.doctor?.permTagsGroupsView !== undefined ? user.doctor.permTagsGroupsView : isAdmin;
    const canEditTags = user?.doctor?.permTagsGroupsEdit !== undefined ? user.doctor.permTagsGroupsEdit : isAdmin;
    const showTagsUI = canViewTags && tagsAvailable;
    const { data: tagsData } = useApi<Tag[]>(showTagsUI ? "/tags" : null);
    const tags = tagsData ?? [];
    const tagMap = useMemo(() => new Map(tags.map((tg) => [tg.id, tg])), [tags]);
    const [showManageTags, setShowManageTags] = useState(false);
    const [showBulkTagsPopup, setShowBulkTagsPopup] = useState(false);
    const [bulkTagIds, setBulkTagIds] = useState<string[]>([]);

    // Additive on the backend ($addToSet) — applying tags to a multi-select never clears
    // tags an invoice already had, only adds the newly picked ones.
    const applyBulkTags = useCallback(async () => {
        if (bulkTagIds.length === 0) { setShowBulkTagsPopup(false); return; }
        try {
            await apiRequest(
                "/invoices/bulk/tags",
                { method: "PATCH", body: JSON.stringify({ ids: [...selectedIds], tagIds: bulkTagIds }) },
                { "Content-Type": "application/json" },
            );
            setToast({ message: t("invoices.bulkTagsApplied", { count: selectedIds.size }), variant: "success" });
            setSelectedIds(new Set());
            refetch();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("tags.assignFailed"), variant: "error" });
        } finally {
            setShowBulkTagsPopup(false);
            setBulkTagIds([]);
        }
    }, [bulkTagIds, selectedIds, refetch, setToast, setSelectedIds, t]);

    const [paywiseConfirmInvoice, setPaywiseConfirmInvoice] = useState<typeof invoices[0] | null>(null);
    const [paywiseSubmitting, setPaywiseSubmitting] = useState(false);

    const handlePaywise = useCallback((invoiceId: string) => {
        const inv = invoices.find((i) => i.id === invoiceId) ?? null;
        setPaywiseConfirmInvoice(inv);
    }, [invoices]);

    const handlePaywiseSubmit = useCallback(async () => {
        if (!paywiseConfirmInvoice) return;
        setPaywiseSubmitting(true);
        try {
            const result = await apiRequest<{ success: boolean; caseId: string }>(
                `/invoices/${paywiseConfirmInvoice.id}/paywise`, { method: "POST" },
            );
            setPaywiseConfirmInvoice(null);
            setToast({ message: t("invoicePage.paywiseSuccess", { caseId: result.caseId }), variant: "success" });
            refetch();
        } catch (err: any) {
            const detail = err?.body?.error ?? err?.body?.message;
            setToast({ message: detail ? `${t("invoicePage.paywiseError")} (${detail})` : t("invoicePage.paywiseError"), variant: "error" });
        } finally {
            setPaywiseSubmitting(false);
        }
    }, [paywiseConfirmInvoice, setToast, t, refetch]);

    const [showUploadPopup, setShowUploadPopup] = useState(false);
    const [showUploadHistory, setShowUploadHistory] = useState(false);
    // Unfinished manual invoices of this member (chapter 18) — the button only shows when there are some.
    const [showDrafts, setShowDrafts] = useState(false);
    const { data: draftsData, refetch: refetchDrafts } = useApi<{ drafts: InvoiceDraftSummary[] }>(canWrite ? "/invoice-drafts" : null);
    const drafts = draftsData?.drafts ?? [];
    const [showCsvExport, setShowCsvExport] = useState(false);

    const [lxColor, setLxColor] = useState(false);
    const [lxDuplex, setLxDuplex] = useState(false);
    const [lxEnvelope, setLxEnvelope] = useState("DIN_LANG_FENSTER");
    const [lxDispatch, setLxDispatch] = useState("");
    const [lxShipping, setLxShipping] = useState("national");
    const [lxPriceData, setLxPriceData] = useState<{ pages: number; price: number } | null>(null);
    const [lxPriceLoading, setLxPriceLoading] = useState(false);

    useEffect(() => {
        if (!lxPopupInvoiceId) return;
        setLxColor(lxDefaults.color);
        setLxDuplex(lxDefaults.duplex);
        setLxEnvelope(lxDefaults.envelope);
        setLxDispatch(lxDefaults.dispatch);
        setLxShipping(lxDefaults.shipping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lxPopupInvoiceId]);

    useEffect(() => {
        if (!lxPopupInvoiceId) return;
        setLxPriceData(null);
        setLxPriceLoading(true);
        const tid = setTimeout(async () => {
            try {
                const params = new URLSearchParams({
                    invoiceId:  lxPopupInvoiceId,
                    color:      lxColor ? "1" : "0",
                    duplex:     lxDuplex ? "1" : "0",
                    envelope:   lxEnvelope,
                    shipping:   lxShipping,
                    registered: lxDispatch || "",
                });
                const data = await apiRequest<{ pages: number; price: number }>(`/notifications/letterxpress-price?${params}`, {}, {});
                setLxPriceData(data);
            } catch {
                setLxPriceData(null);
            } finally {
                setLxPriceLoading(false);
            }
        }, 300);
        return () => clearTimeout(tid);
    }, [lxPopupInvoiceId, lxColor, lxDuplex, lxEnvelope, lxShipping, lxDispatch]);

    // All-statuses filtered list (search + other filters, no showPaid/showWrittenOff exclusion)
    const baseFiltered = useMemo(() => {
        const list = applyInvoiceFilters(invoices, search, filters, requireLegalAcceptance);
        if (!sortKey) return list;
        return [...list].sort((a, b) => {
            const av = getSortValue(a, sortKey as InvoiceSortKey);
            const bv = getSortValue(b, sortKey as InvoiceSortKey);
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [invoices, search, filters, sortKey, sortDir, requireLegalAcceptance]);

    const viewCounts = useMemo(() => computeInvoiceViewCounts(baseFiltered), [baseFiltered]);

    const displayedInvoices = useMemo(
        () => baseFiltered.filter((i) => matchesInvoiceView(i, viewKey)),
        [baseFiltered, viewKey],
    );
    // What the list shows, in its order, across all pages — "next invoice" on the invoice page follows it.
    const navRows = useMemo(() => invoiceNavRows(displayedInvoices), [displayedInvoices]);

    const {
        page,
        setPage,
        pageSize,
        setPageSize,
        totalPages,
        pagedItems: pagedDisplayedInvoices,
        PAGE_SIZES,
    } = useClientPagination(displayedInvoices, [search, filters, viewKey, sortKey, sortDir]);

    const handleExportCsv = useCallback(() => setShowCsvExport(true), []);

    const now = new Date();
    const eyebrowTime = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const totalCount = viewCounts.all;

    const th = (label: React.ReactNode, key: InvoiceSortKey, extraClass?: string) => (
        <th className={`sortable${extraClass ? ` ${extraClass}` : ""}`} onClick={() => handleSort(key)}>
            <div className="th-content">
                {label}
                <SortIcon column={key} sortKey={sortKey} sortDir={sortDir} />
            </div>
        </th>
    );

    return (
        <>
        {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
        <div className="page page--roster invoices-page">
            <div className="roster-body">
            <PageHeader
                introOnly
                sticky
                loading={loading}
                eyebrow={t("nav.invoices")}
                eyebrowIcon={<TbFileEuro />}
                eyebrowExtras={loading ? undefined : [
                    t("common.updatedAt", { time: eyebrowTime, defaultValue: `Updated ${eyebrowTime}` }),
                ]}
                title={t("invoices.title")}
                count={loading ? undefined : totalCount}
                lead={
                    !loading && totalCount > 0 ? (
                        <Trans
                            i18nKey="invoices.lead"
                            values={{ total: totalCount, open: viewCounts.open, overdue: viewCounts.overdue, unsent: viewCounts.unsent }}
                            components={{ strong: <strong />, em: <em /> }}
                        />
                    ) : undefined
                }
                search={search}
                setSearch={setSearch}
                placeholder={t("invoices.search")}
                primaryAction={canWrite ? {
                    label: <><FaUpload /> {t("invoices.upload")}</>,
                    onClick: () => setShowUploadPopup(true),
                } : undefined}
                secondaryAction={canWrite ? (
                    <button
                        className="page-header__cta page-header__cta--create"
                        onClick={() => navigate("/invoices/new")}
                    >
                        <TbFilePlus /> {t("invoiceCreate.entry")}
                    </button>
                ) : undefined}
                // Tag management is occasional setup, not a daily action — it lives in the menu
                // so the header row keeps a clear Search → Create → Upload hierarchy.
                overflowItems={[
                    ...(showTagsUI && canEditTags ? [
                        { label: <><TbTag /> {t("tags.manageTags")}</>, onClick: () => setShowManageTags(true) },
                    ] : []),
                    ...(canExportCsv ? [
                        { label: <><TbDownload /> {t("invoices.exportCsv", { defaultValue: "Export CSV/DATEV" })}</>, onClick: handleExportCsv },
                    ] : []),
                ]}
                accentColor="#f59e0b"
            />

            <RosterSubBar
                loading={loading}
                tabs={[
                    {
                        key: "all",
                        label: t("invoices.viewAll", { defaultValue: "All" }),
                        count: viewCounts.all,
                        active: viewKey === "all",
                        onClick: () => setViewKey("all"),
                    },
                    {
                        key: "open",
                        label: t("invoices.viewOpen", { defaultValue: "Open" }),
                        count: viewCounts.open,
                        active: viewKey === "open",
                        onClick: () => setViewKey("open"),
                    },
                    {
                        key: "overdue",
                        label: t("invoices.viewOverdue", { defaultValue: "Overdue" }),
                        count: viewCounts.overdue,
                        warn: true,
                        active: viewKey === "overdue",
                        onClick: () => setViewKey("overdue"),
                    },
                    {
                        key: "unsent",
                        label: t("invoices.viewUnsent", { defaultValue: "Not sent" }),
                        count: viewCounts.unsent,
                        active: viewKey === "unsent",
                        onClick: () => setViewKey("unsent"),
                    },
                    {
                        key: "paid",
                        label: t("invoices.viewPaid", { defaultValue: "Paid" }),
                        count: viewCounts.paid,
                        active: viewKey === "paid",
                        onClick: () => setViewKey("paid"),
                    },
                    {
                        key: "writtenOff",
                        label: t("invoices.viewWrittenOff", { defaultValue: "Written off" }),
                        count: viewCounts.writtenOff,
                        active: viewKey === "writtenOff",
                        onClick: () => setViewKey("writtenOff"),
                    },
                ]}
                subRightActions={
                    <>
                        {drafts.length > 0 && (
                            <button className="upload-hist-btn invoice-drafts-btn" onClick={() => setShowDrafts(true)}>
                                <TbFilePencil /> {t("invoiceDrafts.button")}
                                <span className="invoice-drafts-btn__count">{drafts.length}</span>
                            </button>
                        )}
                        <button className="upload-hist-btn" onClick={() => setShowUploadHistory(true)}>
                            <TbHistory /> {t("invoices.uploadHistory")}
                        </button>
                        <InvoiceFilterBar filters={filters} onChange={setFilters} onClearSearch={() => setSearch("")} isPro={isPro} tags={showTagsUI ? tags : undefined} postEnabled={postEnabled} doctors={doctorOptions} />
                    </>
                }
                accentColor="#f59e0b"
            />

            {!loading && displayedInvoices.length > 0 && (
                <TablePagination
                    placement="roster-top"
                    loading={loading}
                    accentColor="#f59e0b"
                    page={page}
                    setPage={(p) => { setSelectedIds(new Set()); setPage(p); }}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    totalPages={totalPages}
                    totalCount={displayedInvoices.length}
                    pageSizes={PAGE_SIZES}
                />
            )}

            {lxStatusJob && (() => {
                const statusMap: Record<string, string> = {
                    draft: "Draft (test mode)",
                    hold: "On hold",
                    queue: "In queue",
                    done: "Sent",
                    canceled: "Canceled",
                };
                return (
                    <Popup
                        title={`Letter Job #${lxStatusJob.id}`}
                        message={
                            <div className="popup-detail">
                                <div><strong>Status:</strong> {statusMap[lxStatusJob.status] ?? lxStatusJob.status}</div>
                                {lxStatusJob.dispatch_date && <div><strong>Dispatch date:</strong> {lxStatusJob.dispatch_date}</div>}
                                {lxStatusJob.created_at && <div><strong>Submitted:</strong> {new Date(lxStatusJob.created_at).toLocaleString()}</div>}
                                {lxStatusJob.items?.[0]?.amount != null && <div><strong>Cost:</strong> €{(Number(lxStatusJob.items[0].amount) + Number(lxStatusJob.items[0].vat ?? 0)).toFixed(2)}</div>}
                            </div>
                        }
                        actions={[{ label: "OK", variant: "primary", onClick: () => setLxStatusJob(null) }]}
                        onClose={() => setLxStatusJob(null)}
                    />
                );
            })()}
            {lxPopupInvoiceId && (
                <Popup
                    className="popup--wide"
                    title={t("invoicePage.sendViaPost")}
                    message={(() => {
                        const inv = invoices.find((i) => i.id === lxPopupInvoiceId);
                        const r = inv?.recipient;
                        const p = lxPraxis;
                        const salutation = r?.salutation?.toLowerCase().startsWith("h") ? "Herrn" : r?.salutation?.toLowerCase().startsWith("f") ? "Frau" : undefined;
                        const toLines = r
                            ? [
                                [salutation, r.title, r.firstName, r.lastName].filter(Boolean).join(" "),
                                r.street,
                                [r.plz, r.city].filter(Boolean).join(" "),
                              ].filter(Boolean)
                            : [];
                        const fromLines = p
                            ? [
                                p.name,
                                p.street,
                                [p.plz, p.city].filter(Boolean).join(" "),
                              ].filter(Boolean)
                            : [];
                        return (
                            <div className="lx-options">
                                <div className="lx-options__banner lx-options__banner--warn lx-options__banner--sticky">
                                    <MdOutlineWarningAmber size={16} />
                                    {t("settings.postBillingNotice")}
                                </div>
                                <div className="lx-options__banner lx-options__banner--neutral lx-options__banner--sticky">
                                    <MdOutlineInfo size={16} />
                                    <span>
                                        {t("settings.postEstimatedCost")}{" "}
                                        {lxPriceLoading
                                            ? "…"
                                            : lxPriceData != null
                                                ? <>€{lxPriceData.price.toFixed(2)}{" "}({lxPriceData.pages} pages)</>
                                                : "—"
                                        }
                                    </span>
                                </div>
                                {(toLines.length > 0 || fromLines.length > 0) && (
                                    <div className="lx-options__address-row">
                                        {fromLines.length > 0 && (
                                            <div className="lx-options__group">
                                                <span className="lx-options__label">{t("settings.postWillBeSentFrom")}</span>
                                                <address className="lx-options__address">
                                                    {fromLines.map((line, i) => (
                                                        <span key={i}>{line}</span>
                                                    ))}
                                                </address>
                                            </div>
                                        )}
                                        {toLines.length > 0 && (
                                            <div className="lx-options__group">
                                                <span className="lx-options__label">{t("settings.postWillBeSentTo")}</span>
                                                <address className="lx-options__address">
                                                    {toLines.map((line, i) => (
                                                        <span key={i}>{line}</span>
                                                    ))}
                                                </address>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="lx-options__row">
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postColor")}</span>
                                        <div className="lx-options__choices">
                                            <label className={`lx-options__choice${!lxColor ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxColor" checked={!lxColor} onChange={() => setLxColor(false)} />
                                                {t("settings.postColorBW")}
                                            </label>
                                            <label className={`lx-options__choice${lxColor ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxColor" checked={lxColor} onChange={() => setLxColor(true)} />
                                                {t("settings.postColorOption")}
                                            </label>
                                        </div>
                                    </div>
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postPrint")}</span>
                                        <div className="lx-options__choices">
                                            <label className={`lx-options__choice${!lxDuplex ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDuplex" checked={!lxDuplex} onChange={() => setLxDuplex(false)} />
                                                {t("settings.postPrintSimplex")}
                                            </label>
                                            <label className={`lx-options__choice${lxDuplex ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDuplex" checked={lxDuplex} onChange={() => setLxDuplex(true)} />
                                                {t("settings.postPrintDuplex")}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="lx-options__row">
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postEnvelope")}</span>
                                        <div className="lx-options__choices">
                                            {([
                                                { value: "DIN_LANG_FENSTER_LINKS", tKey: "postEnvelopeWindowLeft" },
                                                { value: "C4_OHNE_FENSTER",        tKey: "postEnvelopeC4" },
                                            ] as const).map(({ value, tKey }) => (
                                                <label key={value} className={`lx-options__choice${lxEnvelope === value ? " lx-options__choice--selected" : ""}`}>
                                                    <input type="radio" name="lxEnvelope" checked={lxEnvelope === value} onChange={() => setLxEnvelope(value)} />
                                                    {t(`settings.${tKey}`)}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="lx-options__group">
                                        <span className="lx-options__label">{t("settings.postShipping")}</span>
                                        <div className="lx-options__choices">
                                            {([["national", "postShippingNational"], ["international", "postShippingInternational"]] as const).map(([val, key]) => {
                                                const disabledIntl = val === "international" && (lxDispatch === "r1" || lxDispatch === "r2");
                                                return (
                                                    <label key={val} className={`lx-options__choice${lxShipping === val ? " lx-options__choice--selected" : ""}${disabledIntl ? " lx-options__choice--disabled" : ""}`}>
                                                        <input type="radio" name="lxShipping" checked={lxShipping === val} disabled={disabledIntl} onChange={() => setLxShipping(val)} />
                                                        {t(`settings.${key}`)}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="lx-options__group">
                                    <span className="lx-options__label">{t("settings.postRegisteredMail")}</span>
                                    <div className="lx-options__choices">
                                        {([["", "postRegisteredMailNone"], ["r1", "postRegisteredMailR1"], ["r2", "postRegisteredMailR2"]] as const).map(([val, key]) => (
                                            <label key={val} className={`lx-options__choice${lxDispatch === val ? " lx-options__choice--selected" : ""}`}>
                                                <input type="radio" name="lxDispatch" checked={lxDispatch === val} onChange={() => {
                                                    setLxDispatch(val);
                                                    if ((val === "r1" || val === "r2") && lxShipping === "international") setLxShipping("national");
                                                }} />
                                                {t(`settings.${key}`)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => { setLxPopupInvoiceId(null); setLxPriceData(null);} },
                        { label: pdfLoading ? t("invoicePage.generating") : t("invoicePage.viewPdf"), variant: "default", disabled: pdfLoading, onClick: () => viewPdf(lxPopupInvoiceId) },
                        {
                            label: t("recipients.send"), variant: "primary", onClick: () => {
                                sendViaLetterXpress(lxPopupInvoiceId, lxColor, lxDuplex, lxEnvelope, lxDispatch, lxShipping);
                                setLxPopupInvoiceId(null);
                                setLxColor(false);
                                setLxDuplex(false);
                                setLxEnvelope("DIN_LANG_FENSTER");
                                setLxDispatch("");
                                setLxShipping("national");
                                setLxPriceData(null);
                            },
                        },
                    ]}
                    onClose={() => { setLxPopupInvoiceId(null); setLxPriceData(null);}}
                />
            )}
            {pdfUrl && createPortal(
                <div className="pdf-modal-overlay" onClick={closePdf}>
                    <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pdf-modal__header">
                            <span>Invoice PDF</span>
                            <button className="pdf-modal__close" onClick={closePdf}>✕</button>
                        </div>
                        {(pdfLoading || pdfFrameLoading) && (
                            <div className="pdf-modal__loading">
                                <span className="pdf-modal__spinner" />
                                <span>{t("invoicePage.generating")}</span>
                            </div>
                        )}
                        <iframe
                            className="pdf-modal__frame"
                            src={pdfUrl}
                            title="Invoice PDF"
                            onLoad={() => setPdfFrameLoading(false)}
                        />
                    </div>
                </div>,
                document.body,
            )}
            {bulkPopup && (
                <Popup
                    title={bulkPopup.title}
                    message={bulkPopup.message}
                    className={bulkPopup.className}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setBulkPopup(null) },
                        { label: bulkPopup.confirmLabel, variant: bulkPopup.confirmVariant, disabled: bulkPopup.confirmDisabled, onClick: () => { bulkPopup.action(); setBulkPopup(null); } },
                    ]}
                    onClose={() => setBulkPopup(null)}
                />
            )}

            {paywiseConfirmInvoice && (
                <Popup
                    title={t("invoicePage.paywiseConfirmTitle")}
                    message={
                        <div className="paywise-confirm">
                            <p className="paywise-confirm__desc">{t("invoicePage.paywiseConfirmDesc")}</p>
                            <ul className="paywise-confirm__list">
                                <li><strong>{t("invoicePage.paywiseConfirmDataName")}:</strong> {[paywiseConfirmInvoice.recipient?.firstName, paywiseConfirmInvoice.recipient?.lastName].filter(Boolean).join(" ") || "—"}</li>
                                <li><strong>{t("invoicePage.paywiseConfirmDataAddress")}:</strong> {[paywiseConfirmInvoice.recipient?.street, (paywiseConfirmInvoice.recipient as any)?.number, paywiseConfirmInvoice.recipient?.plz, paywiseConfirmInvoice.recipient?.city].filter(Boolean).join(", ") || "—"}</li>
                                <li><strong>{t("invoicePage.paywiseConfirmDataContact")}:</strong> {[paywiseConfirmInvoice.recipient?.email, paywiseConfirmInvoice.recipient?.tel?.[0]].filter(Boolean).join(" / ") || "—"}</li>
                                <li><strong>{t("invoicePage.paywiseConfirmDataAmount")}:</strong> €{((paywiseConfirmInvoice.amount ?? 0) + (paywiseConfirmInvoice.dunningFineAmount ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</li>
                                <li><strong>{t("invoicePage.paywiseConfirmDataRef")}:</strong> {paywiseConfirmInvoice.readableId}</li>
                            </ul>
                            <p className="paywise-confirm__note">{t("invoicePage.paywiseConfirmNote")}</p>
                        </div>
                    }
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setPaywiseConfirmInvoice(null) },
                        { label: paywiseSubmitting ? "…" : t("invoicePage.paywiseConfirmBtn"), variant: "primary", disabled: paywiseSubmitting, onClick: handlePaywiseSubmit },
                    ]}
                    onClose={() => setPaywiseConfirmInvoice(null)}
                />
            )}

            {selectedIds.size > 0 && (() => {
                const selectedInvoices = invoices.filter((i) => selectedIds.has(i.id));
                // Enabled whenever anything is still open: the dialog explains why the rest is skipped
                // (no consent, no address, already sent) and disables its own send button if nothing is left.
                const letterSendableCount = selectedInvoices.filter(isStatusSendable).length;
                const patientNames = [...new Set(
                    selectedInvoices
                        .map((i) => i.patient ? `${i.patient.firstName ?? ""} ${i.patient.lastName ?? ""}`.trim() : null)
                        .filter(Boolean) as string[]
                )];
                const namePreview = patientNames.slice(0, 2).join(", ") + (patientNames.length > 2 ? ` +${patientNames.length - 2}` : "");
                return (
                <div className="bulk-bar">
                    <span className="bulk-bar__count">
                        {selectedIds.size} {t("invoices.selected")}
                        {namePreview && <span className="bulk-bar__ids"> ({namePreview})</span>}
                    </span>
                    {/* No permEdit check on the send-link endpoint — see InvoicePage.tsx */}
                    <button className="bulk-bar__btn bulk-bar__btn--send" onClick={bulkSend}>
                        <TbLink /> {t("invoices.sendPortalLink")}
                    </button>
                    {canWrite && postEnabled && !isTrialing && (
                        <button className="bulk-bar__btn bulk-bar__btn--post" onClick={bulkSendLetter} disabled={letterSendableCount === 0}>
                            <SlEnvolopeLetter /> {t("invoices.sendViaPost")}
                        </button>
                    )}
                    {showTagsUI && canEditTags && (
                        <button className="bulk-bar__btn bulk-bar__btn--tag" onClick={() => setShowBulkTagsPopup(true)}>
                            <TbTag /> {t("invoices.addTags")}
                        </button>
                    )}
                    {canDelete && (
                        <>
                            <span className="bulk-bar__sep" />
                            <button className="bulk-bar__btn bulk-bar__btn--delete" onClick={bulkDelete}>
                                <RiDeleteBin2Line /> {t("form.delete")}
                            </button>
                        </>
                    )}
                    <span className="bulk-bar__sep" />
                    <button className="bulk-bar__btn bulk-bar__btn--clear" onClick={() => setSelectedIds(new Set())}>×</button>
                </div>
                );
            })()}

            {showBulkTagsPopup && (
                <InvoiceTagsPopup
                    tags={tags}
                    currentTagIds={bulkTagIds}
                    onChange={setBulkTagIds}
                    onClose={applyBulkTags}
                />
            )}

            <div className={`table-container${bulkDeleting ? " table-container--busy" : ""}`}>
                    {bulkDeleting && (
                        <div className="table-busy-overlay">
                            <div className="table-busy-overlay__inner">
                                <span className="table-busy-spinner" />
                                <span className="table-busy-label">
                                    {deletePhase === "deleting"
                                        ? t("common.deletingData", { count: deleteCount })
                                        : t("common.cleaningUp")}
                                </span>
                            </div>
                        </div>
                    )}
                {loading ? (
                    <TableSkeleton cols={10} />
                ) : error ? (
                    <div className="empty-state">{error}</div>
                ) : (
                    <table className="dashboard-table invoices-table">
                        <thead>
                            <tr>
                                <th className="row-check">
                                    <input
                                        type="checkbox"
                                        checked={pagedDisplayedInvoices.length > 0 && pagedDisplayedInvoices.every((i) => selectedIds.has(i.id))}
                                        onChange={() => toggleSelectAll(pagedDisplayedInvoices.map((i) => i.id))}
                                        title={t("invoices.selectAll")}
                                    />
                                </th>
                                {th(t("table.id"), "id")}
                                <th>{t("table.status")}</th>
                                {th(`${t("table.created")} · ${t("table.sent")} · ${t("table.due")}`, "creationDate", "col-dates")}
                                {th(t("table.remaining"), "remaining")}
                                {showTagsUI && <th>{t("tags.tag")}</th>}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedDisplayedInvoices.length === 0 ? (
                                <tr><td colSpan={showTagsUI ? 7 : 6}><EmptyState message={t("invoices.noFound")} /></td></tr>
                            ) : pagedDisplayedInvoices.map((invoice) => (
                                <InvoiceRow
                                    key={invoice.id}
                                    invoice={invoice}
                                    selected={selectedIds.has(invoice.id)}
                                    onToggle={() => toggleSelect(invoice.id)}
                                    onSend={sendPortalLink}
                                    onSendPost={canWrite ? openLxPopup : undefined}
                                    onCheckLetterStatus={checkLetterStatus}
                                    postEnabled={canWrite && postEnabled && !isTrialing}
                                    emailEnabled={emailEnabled}
                                    questionsEnabled={questionsEnabled}
                                    requireLegalAcceptance={requireLegalAcceptance}
                                    showTags={showTagsUI}
                                    tagMap={tagMap}
                                    showOwner={multiDoctor}
                                    onDelete={deleteInvoice}
                                    canDelete={canDelete}
                                    showPaywise={showPaywise}
                                    onPaywise={canWrite ? handlePaywise : undefined}
                                    navRows={navRows}
                                    onCardPaid={refetch}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showUploadHistory && (
                <UploadHistoryPopup onClose={() => setShowUploadHistory(false)} />
            )}
            {showDrafts && (
                <InvoiceDraftsPopup drafts={drafts} onClose={() => setShowDrafts(false)} onChanged={() => void refetchDrafts()} />
            )}
            {showManageTags && (
                <ManageTagsPopup onClose={() => setShowManageTags(false)} />
            )}
            {showUploadPopup && (
                <PadUploadPopup
                    onClose={() => setShowUploadPopup(false)}
                    onSuccess={(sendFailureCount) => setToast(
                        sendFailureCount && sendFailureCount > 0
                            ? { message: t("fileUpload.uploadSuccessWithFailures", { count: sendFailureCount }), variant: "error" }
                            : { message: t("fileUpload.uploadSuccess"), variant: "success" },
                    )}
                    tags={showTagsUI ? tags : undefined}
                />
            )}
            {showCsvExport && (
                <CsvExportPopup onClose={() => setShowCsvExport(false)} tags={showTagsUI ? tags : undefined} />
            )}
            </div>
        </div>
        </>
    );
}
